import { useCallback, useMemo, useState } from 'react'

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useBankPortalContext } from 'bank-portal-context'
import { BeforeFormChangeHandler, useFormData } from 'brief-form'
import {
  ContactListWebPageRequest,
  ContactListWebPageResponse,
  ContactListWebPageResponseItem
} from 'commons/types/contracts/api/bank/contactList'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { useTableState } from 'ui/components/Table'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

export const useContactList = () => {
  const {
    layout: { assignedCompaniesIDs }
  } = useBankPortalContext()

  const queryClient = useQueryClient()

  const defaultFilter: ContactListWebPageRequest = useMemo(
    () => ({
      company_id: assignedCompaniesIDs.length > 0 ? 'my' : 'all',
      relationships_type: assignedCompaniesIDs.length > 0 ? 'my' : 'all',
      page: 1,
      size: 10,
      searchQuery: '',
      type: 'all'
    }),
    []
  )

  const tableState = useTableState({
    filter: {}
  })

  const onBeforeChange: BeforeFormChangeHandler<ContactListWebPageRequest> = useCallback(
    ({ value, errors, oldValue }) => {
      const filterChanged =
        value.relationships_type !== oldValue.relationships_type ||
        value.company_id !== oldValue.company_id ||
        value.type !== oldValue.type ||
        value.searchQuery !== oldValue.searchQuery

      if (filterChanged) value.page = 1

      return {
        value,
        errors
      }
    },
    []
  )

  const filterForm = useFormData<ContactListWebPageRequest>({
    initialValue: defaultFilter,
    onBeforeChange
  })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      const newFilter: ContactListWebPageRequest = {
        ...filterForm.config.value,
        page,
        size
      }
      filterForm.set({ value: newFilter })
      queryClient.invalidateQueries({ queryKey: ['contact-list', newFilter] })
    },
    [filterForm.config]
  )

  const payload = {
    ...filterForm.config.value,
    relationships_type:
      typeof filterForm.config.value?.company_id === 'string' ? filterForm.config.value?.company_id : undefined,
    company_id:
      typeof filterForm.config.value?.company_id === 'number' ? filterForm.config.value?.company_id : undefined
  }

  const { data, isFetching } = useQuery<ContactListWebPageRequest, Error, ContactListWebPageResponse>({
    queryFn: () => Request.post(Request.urls.bank.contactListWebPage, payload),
    queryKey: ['contact-list', payload],
    placeholderData: keepPreviousData
  })

  const [contactForDelete, setContactForDelete] = useState<ContactListWebPageResponseItem | undefined>(undefined)

  const { mutate: deleteContact } = useMutation<void, Error, { id: number }>({
    mutationFn: ({ id }) => Request.delete(Request.urls.bank.contact, { id }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['contact-list'] })
      Toast.successSmall('Contact was deleted.')
    },
    onError: (error) => handlerServerError(error)
  })

  const { open, Confirmation: DeleteConfirmation } = useConfirmation({
    message: `Are you sure you want to delete ${contactForDelete?.contactInfo} contact?`,
    onConfirm: () => {
      if (contactForDelete) {
        deleteContact({ id: contactForDelete.id })
      }
    },
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const startContactDelete = useCallback(
    (item: ContactListWebPageResponseItem) => {
      setContactForDelete(item)
      open()
    },
    [setContactForDelete, open]
  )

  return {
    webPageData: data,
    tableState,
    isFetching,
    filterForm,
    changePagination,
    DeleteConfirmation,
    startContactDelete
  }
}
